import React from "react";
import { cn } from "@/lib/utils";

/**
 * SectionHeading — UI-2C4 shared in-page section heading.
 *
 * Standardises the H2/H3 scale and spacing for informational sections without
 * forcing page layouts to change. Headings inherit Manrope + tracking from the
 * global `h2/h3` rules; this component just fixes size, weight and spacing.
 *
 * IMPORTANT: never renders an <h1> — pages already get their single H1 from
 * `PageHeader`. Use `as="h2"` (default) or `as="h3"`.
 *
 * Props:
 * - as             "h2" | "h3"
 * - title          string | node
 * - description    optional supporting line
 * - action         optional existing action (button/link)
 * - align          "left" | "center"
 * - className       wrapper classes
 * - titleClassName  override/extend the title size (e.g. larger hero sections)
 */
const sizeByLevel = {
  h2: "text-2xl sm:text-3xl",
  h3: "text-xl sm:text-2xl",
};

export function SectionHeading({
  as = "h2",
  title,
  description,
  action,
  align = "left",
  className,
  titleClassName,
  ...props
}) {
  const Tag = as === "h3" ? "h3" : "h2";
  const centered = align === "center";
  return (
    <div
      data-testid="section-heading"
      className={cn(
        "mb-6",
        action && !centered
          ? "flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
          : "",
        centered ? "text-center" : "text-left",
        className
      )}
      {...props}
    >
      <div className={cn("flex flex-col gap-2", centered && "items-center")}>
        <Tag
          data-testid="section-heading-title"
          className={cn(
            sizeByLevel[Tag],
            "font-bold tracking-tight text-[var(--text-main)]",
            titleClassName
          )}
        >
          {title}
        </Tag>
        {description && (
          <p
            className={cn(
              "max-w-2xl text-base text-[var(--text-muted)]",
              centered && "mx-auto"
            )}
          >
            {description}
          </p>
        )}
      </div>
      {action && <div className={cn("shrink-0", centered && "mt-4")}>{action}</div>}
    </div>
  );
}

export default SectionHeading;
